/* eslint-disable import/no-cycle */
import { IAlert } from './Alert';
import { IApiList } from './Api';
import { CalculatorParticipantType } from './CalculatorRule';
import { ICardDetails } from './Card';
import { DisbursementMethod, EmployeeStatus, EventActionTypes, IEmployee } from './Employee';
import { IJob } from './Job';
import { IBaseSite } from './Site';
import { ISubmittedBy } from './User';

export enum PayoutStatus {
  PAID = 'paid',
  PENDING = 'pending',
  EXPIRED = 'expired',
  CANCELED = 'canceled',
  PAYROLL = 'payroll',
  CASH = 'cash',
}

export const PAYOUT_STATUSES_COUNTED_IN_TOTALS = [PayoutStatus.PAID, PayoutStatus.PAYROLL, PayoutStatus.CASH];

export const PAYOUT_STATUS_DISPLAY_ORDER = [PayoutStatus.PAID, PayoutStatus.PAYROLL, PayoutStatus.CASH, PayoutStatus.PENDING, PayoutStatus.EXPIRED, PayoutStatus.CANCELED];

export enum PayoutApprovalStatus {
  AWAITING_CHECKS = 'awaiting_checks',
  NOT_REQUIRED = 'not_required',
  REVIEW_REQUIRED = 'review_required',
  REVIEWER_APPROVED = 'reviewer_approved',
  REVIEWER_REJECTED = 'reviewer_rejected',
}

export enum PayoutApprovalFailedCheckStatus {
  LAST_PAYMENT = 'lastPayment',
  DAILY_VELOCITY_LIMIT = 'dailyVelocityLimit',
}

export enum CurrencyType {
  USD = 'usd',
}

export enum PaymentType {
  TIP = 'tip',
}

export enum PayoutRefType {
  CALCULATOR = 'calculator',
}

export interface IPayoutOverviewTotalsDisplay {
  cash: boolean;
  holdbacks: boolean;
  instant: boolean;
  payroll: boolean;
}

interface IPayoutEmployee {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  status: EmployeeStatus;
  deletedAt?: string;
}

export interface IPayoutEmployeeWithholdings {
  id: string;
  amount: number;
  currentPayroll: number;
  settings: {
    cap?: number;
    minimum: number;
    rate: number;
    type: string;
  };
}

export interface IPayoutWithholdings {
  employee: IPayoutEmployeeWithholdings;
  payPeriod: IPayPeriod;
}

export interface IPayout {
  id: string;
  alert?: IAlert;
  approvalCheck: IPayoutApprovalCheck;
  cancelReason?: string;
  currency: CurrencyType;
  autoGratuity: number;
  disbursementMethod: DisbursementMethod;
  employee: IPayoutEmployee;
  expiresAt?: string;
  gross: number;
  holdback: number;
  job: IJob;
  net: number;
  notes?: string;
  paidAt?: string;
  paymentDate: string;
  payPeriodClosed?: boolean;
  payPeriod?: IPayPeriod;
  paymentDestination?: ICardDetails;
  paymentId: string;
  refType?: PayoutRefType;
  refId?: string;
  site: IBaseSite;
  status: PayoutStatus;
  statusHistory?: IPayoutStatusHistory[];
  type: PaymentType;
  userId?: string;
  withholdings?: IPayoutWithholdings;
  createdAt?: string;
  updatedAt?: string;
}

export interface IPayoutApprovalCheck {
  status: PayoutApprovalStatus;
  statusDesc?: string;
  reviewers: {
    id: string;
    name: string;
    type: string;
    status: PayoutApprovalStatus;
  }[];
  failedChecks: {
    name: PayoutApprovalFailedCheckStatus;
    approvalRequired: boolean;
  }[];
}

export interface IPayoutGroup {
  id: string;
  paymentDate: string;
  site: IBaseSite;
  notes?: string;
  payouts: IPayout[];
}

export interface IReviewPayout {
  status?: PayoutApprovalStatus;
  desc: string;
}

export interface ICancelPayoutOptions {
  cancelReason: string;
}

export interface IChangeDisbursementMethodOptions {
  type: DisbursementMethod;
  reason?: string;
}

interface IPayoutStatusHistory {
  action: EventActionTypes;
  status: PayoutStatus;
  changedBy: ISubmittedBy;
  createdAt: string;
}

export interface IPayoutsPolicies {
  lastPayment?: ILastPaymentPolicies;
  dailyVelocityLimit?: IDailyVelocityLimitPolicies;
}

enum ApprovalPoliciesStatusTypes {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

interface IThresholdBasedPaymentPolicies {
  status: ApprovalPoliciesStatusTypes;
  threshold: number;
}

interface ILastPaymentPolicies extends IThresholdBasedPaymentPolicies {}

interface IDailyVelocityLimitPolicies extends IThresholdBasedPaymentPolicies {}

export interface IBasePayoutState {
  net: number;
  gross: number;
  holdbacks: number;
  count: number;
  instant: number;
  payroll: number;
  autoGratuity: number;
  cash: number;
}

export interface IPayoutStateByDate extends IBasePayoutState {
  date: string;
  byStatus?: IPayoutStateByStatus[];
}

export interface IPayoutStateByStatus extends IBasePayoutState {
  status: PayoutStatus;
}

export interface IPayoutStateByParticipant {
  autoGratuities: number;
  count: number;
  tips: number;
  total: number;
  name: string;
  type: CalculatorParticipantType;
}

export interface IPayoutStats {
  byDate: IPayoutStateByDate[];
  byStatus: IPayoutStateByStatus[];
  byParticipant: IPayoutStateByParticipant[];
}

interface IEmployeePayrollTipStatusCount {
  paid: number;
  payroll: number;
  pending: number;
  canceled: number;
  expired: number;
  cash: number;
}

export interface IEmployeePayrollTip {
  organizationId: string;
  siteId: string;
  paymentId: string;
  gross: number;
  holdback: number;
  net: number;
  direct: number;
  indirect: number;
  instant: { total: number; tips: number; autoGratuities: number };
  payroll: { total: number; tips: number; autoGratuities: number };
  cash: { total: number; tips: number; autoGratuities: number };
  statusCounts: IEmployeePayrollTipStatusCount;
  employee: IEmployee;
}

export interface IGetEmployeePayoutsResponse extends IApiList<IPayout> {
  employee: IEmployee;
}

export enum PayPeriodStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  REOPENED = 'reopened',
}

export enum PayPeriodAction {
  CLOSE = 'close',
  REOPEN = 'reopen',
}

export interface IPayPeriodClosureState {
  status: PayPeriodStatus;
  closedAt?: string;
  closedBy?: ISubmittedBy;
  reopenedAt?: string;
  reopenedBy?: ISubmittedBy;
}

export interface IPayPeriod extends Partial<IPayPeriodClosureState> {
  start: string;
  end: string;
  closeable?: boolean;
}

export interface IPayPeriodAmounts {
  tips: number;
  autograts: number;
  gross: number;
  holdback: number;
  net: number;
}

export interface IPayPeriodClosureStats {
  paymentCount: number;
  payoutCount: number;
  totals: IPayPeriodAmounts;
  byMethod: Record<DisbursementMethod, IPayPeriodAmounts>;
  byStatus: Record<PayoutStatus, { count: number; net: number }>;
}

export interface IPayPeriodClosureHistory {
  action: PayPeriodAction;
  reason?: string;
  changedBy: ISubmittedBy;
  changedAt: string;
}

// Response of PUT /v1/sites/:site_id/pay_periods
export interface IPayPeriodClosure extends IPayPeriodClosureState {
  startDate: string;
  endDate: string;
  history: IPayPeriodClosureHistory[];
  stats: {
    atClose: IPayPeriodClosureStats;
    // The stats once the close's conversions to payroll are applied, equal to `atClose` when none were.
    afterConversion: IPayPeriodClosureStats;
  };
  createdAt: string;
  updatedAt: string;
}

export interface IUpdatePayPeriodStatusRequest {
  startDate: string;
  endDate: string;
  status: PayPeriodAction;
}

export enum IPayPeriodFrequency {
  BI_WEEKLY = 'bi_weekly',
  SEMI_MONTHLY = 'semi_monthly',
  WEEKLY = 'weekly',
}

export enum IFirstPayDay {
  FIRST = '1',
  FIFTEENTH = '15',
}

export interface IPayPeriods {
  anchorDate: string;
  frequency: IPayPeriodFrequency;
  payDay1: IFirstPayDay;
  data: IPayPeriod[];
  rangeStart: string;
  rangeEnd: string;
}

// One mode per request, as account_services validates it: `date` for the containing period, `before` and/or
// `limit` for a page ending before the cursor (newest first), `start` and/or `end` for a range (oldest first).
export interface IPayPeriodsQuery {
  before?: string;
  date?: string;
  end?: string;
  limit?: number;
  start?: string;
}

export interface IExportPayout {
  id: string;
  custEmployeeId: string;
  disbursementMethod: DisbursementMethod;
  firstName: string;
  lastName: string;
  jobTitle: string;
  jobClassification: string;
  gross: number;
  holdback: number;
  net: number;
  autoGratuity: number;
  notes: string;
  paymentDate: string;
  siteName: string;
  status: PayoutStatus;
  submittedBy: string;
  createdAt: string;
  updatedAt: string;
}

export enum PaymentWorkflowTypes {
  STANDARD = 'standard',
  UPLOAD = 'upload',
  CALCULATOR = 'calculator',
}
